/**
 * Deterministic trend / sentiment helpers for the feedback dashboard.
 *
 * There is NO inference here — every value is a plain aggregation of the votes
 * and 1-5 ratings users submit. "Trend" means comparing the first half of a
 * feature's rating series to the second half.
 */
import type { FeatureDailyStats, FeatureTrend } from '@/types/feedback';

export type TrendDirection = 'up' | 'down' | 'steady';

// A rating move smaller than this (in stars) is treated as noise, not a trend.
export const STEADY_BAND = 0.1;

// Default drop (in stars) that lands a feature in "Needs attention".
export const NEEDS_ATTENTION_DROP = 0.3;

/** Net sentiment as a percentage in [-100, 100]: (up - down) / total * 100. */
export function netSentiment(up: number, down: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round(((up - down) / total) * 100);
}

/** Response-count-weighted average rating over a set of daily points. */
function weightedAverage(days: FeatureDailyStats[]): number | null {
  const count = days.reduce((sum, d) => sum + d.count, 0);
  if (count === 0) return null;
  const total = days.reduce((sum, d) => sum + d.averageRating * d.count, 0);
  return total / count;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * First-half vs second-half average rating for a feature's (chronological) series.
 * `delta` is second - first, rounded to one decimal; null when either half is empty.
 */
export function halfSplitDelta(daily: FeatureDailyStats[]): {
  firstAvg: number | null;
  secondAvg: number | null;
  delta: number | null;
} {
  if (daily.length === 0) return { firstAvg: null, secondAvg: null, delta: null };
  const mid = Math.ceil(daily.length / 2);
  const firstAvg = weightedAverage(daily.slice(0, mid));
  const secondAvg = weightedAverage(daily.slice(mid));
  const delta = firstAvg !== null && secondAvg !== null ? round1(secondAvg - firstAvg) : null;
  return { firstAvg, secondAvg, delta };
}

/** Classify a rating delta into up / down / steady using STEADY_BAND. */
export function trendDirection(delta: number | null): TrendDirection {
  if (delta === null) return 'steady';
  if (delta > STEADY_BAND) return 'up';
  if (delta < -STEADY_BAND) return 'down';
  return 'steady';
}

/**
 * Split a feature's daily series into up to `buckets` contiguous chunks and
 * return the weighted average rating of each non-empty chunk — the points for a
 * small sparkline.
 */
export function bucketRatings(daily: FeatureDailyStats[], buckets = 6): number[] {
  if (daily.length === 0) return [];
  const size = Math.max(1, Math.ceil(daily.length / buckets));
  const points: number[] = [];
  for (let i = 0; i < daily.length; i += size) {
    const avg = weightedAverage(daily.slice(i, i + size));
    if (avg !== null) points.push(round1(avg));
  }
  return points;
}

/**
 * Distinct-feature-per-day series derived from the (top-N) per-feature trend.
 * Used only for the "features tracked" KPI sparkline shape, not its headline count.
 */
export function featuresPerDay(perFeatureDaily: FeatureTrend[]): number[] {
  const byDay = new Map<string, Set<string>>();
  for (const trend of perFeatureDaily) {
    for (const day of trend.daily) {
      const set = byDay.get(day.date) ?? new Set<string>();
      set.add(trend.stats.feature);
      byDay.set(day.date, set);
    }
  }
  return [...byDay.keys()].sort().map((date) => byDay.get(date)!.size);
}
